import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";

/**
 * The lineage map: instrument → version in force → the rules that cite it,
 * on one date.
 *
 * The one thing on the regulatory page that is genuinely graph-shaped, and
 * it is a small directed graph, so it is drawn flat: each instrument is a
 * row, its versions lie on one time axis, the version in force on the chosen
 * date is the one lit, and the rules that cite that version hang off it.
 * Move the date and the lit version moves, and the rules with it.
 *
 * Plain SVG, no library. Every mark carries a <title>, so hover names it and
 * a screen reader can read the whole map as a list. Placeholders cite no
 * instrument and are listed beneath rather than hidden: a map that showed
 * only attributed rules would show a rulebook more finished than the one we
 * have.
 */

interface MapRule {
  rule_id: string;
  origin_ref: string | null;
  can_block: boolean;
  summary: string;
}

interface MapVersion {
  id: string;
  name: string;
  token: string | null;
  effective_from: string;
  effective_to: string | null;
  current: boolean;
  in_force_on_date: boolean;
  rules: MapRule[];
}

interface MapLineage {
  concept: string;
  name: string;
  versions: MapVersion[];
}

interface MapResponse {
  as_of: string;
  pack_version: string;
  lineages: MapLineage[];
  unattributed: { rule_id: string; origin: string; origin_ref: string | null; summary: string; pack: string }[];
}

const LABEL_W = 190;
const AXIS_W = 420;
const RULES_W = 250;
const ROW_GAP = 10;
const RULE_H = 18;

function toMs(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getTime();
}

function short(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

export function LineageMap({ workspaceId, asOf }: { workspaceId: string; asOf: string }) {
  const q = useQuery<MapResponse, Error>({
    queryKey: ["regulatory-map", workspaceId, asOf],
    enabled: !!workspaceId && /^\d{4}-\d{2}-\d{2}$/.test(asOf),
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/regulatory/map?as_of=${asOf}`),
  });

  if (q.isLoading) {
    return (
      <div className="play-meta" data-testid="lineage-map-loading">
        <span>Drawing the map for {asOf}…</span>
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="play-meta" data-testid="lineage-map-error">
        <span>
          The map needs GraphIQ live; the offline copy holds rules but not
          instrument lineages.
        </span>
      </div>
    );
  }

  // Read defensively: a stale bundle or a partial answer must render as an
  // empty map, not take the whole page down with it.
  const lineages = (Array.isArray(q.data.lineages) ? q.data.lineages : []).map((l) => ({
    ...l,
    versions: Array.isArray(l.versions) ? l.versions : [],
  }));
  const unattributed = Array.isArray(q.data.unattributed) ? q.data.unattributed : [];
  const as_of = q.data.as_of ?? asOf;
  const dates = lineages.flatMap((l) =>
    l.versions.flatMap((v) => [v.effective_from, v.effective_to].filter((d): d is string => !!d)),
  );
  // Ten years of axis, ending a little past the date asked about. A lineage
  // that began in 2000 would otherwise squeeze this year's instrument into a
  // sliver too thin to hold its own name; anything older is clamped to the
  // left edge and still carries its dates in its title.
  const maxMs = Math.max(...dates.map(toMs), toMs(as_of)) + 180 * 86400000;
  const minMs = Math.max(Math.min(...dates.map(toMs)), maxMs - 10 * 365.25 * 86400000);
  const x = (iso: string): number =>
    LABEL_W + Math.max(0, Math.min(1, (toMs(iso) - minMs) / (maxMs - minMs))) * AXIS_W;
  const xEnd = (iso: string | null): number => (iso ? x(iso) : LABEL_W + AXIS_W);
  const MIN_SEG = 34;
  const clip = (t: string, n = 26): string => (t.length > n ? `${t.slice(0, n - 1)}…` : t);

  // Row height follows the rules hanging off the lit version.
  const rows = lineages.map((l) => {
    const lit = l.versions.find((v) => v.in_force_on_date);
    const n = lit?.rules.length ?? 0;
    return { lineage: l, lit, height: Math.max(34, n * RULE_H + 16) };
  });
  const totalH = rows.reduce((a, r) => a + r.height + ROW_GAP, 0) + 30;
  const width = LABEL_W + AXIS_W + RULES_W + 20;

  let y = 24;
  return (
    <div data-testid="lineage-map" style={{ overflowX: "auto" }}>
      <svg
        width={width}
        height={totalH}
        viewBox={`0 0 ${width} ${totalH}`}
        role="list"
        aria-label={`Instruments and the rules that cite them, as at ${as_of}`}
        style={{ display: "block", fontFamily: "inherit" }}
      >
        {/* The date being asked about. Everything lit is lit because of it. */}
        <line
          x1={x(as_of)} x2={x(as_of)} y1={8} y2={totalH - 8}
          stroke="var(--accent)" strokeWidth={1} strokeDasharray="3 3"
        />
        <text x={x(as_of) + 4} y={14} fontSize={10} fill="var(--accent)">
          as at {as_of}
        </text>

        {rows.map(({ lineage, lit, height }) => {
          const top = y;
          y += height + ROW_GAP;
          const mid = top + Math.min(height, 34) / 2;
          return (
            <g key={lineage.concept} role="listitem">
              <title>{lineage.name}</title>
              <text x={0} y={mid + 4} fontSize={12} fill="var(--text)" fontWeight={500}>
                {lineage.name}
              </text>
              {/* The time axis for this lineage. */}
              <line
                x1={LABEL_W} x2={LABEL_W + AXIS_W} y1={mid} y2={mid}
                stroke="var(--border)" strokeWidth={1}
              />
              {lineage.versions.map((v) => {
                const x1 = x(v.effective_from);
                // Wide enough to carry its label even when it began last month.
                const x2 = Math.max(xEnd(v.effective_to), x1 + MIN_SEG);
                const on = v.in_force_on_date;
                return (
                  <g key={v.id} role="listitem">
                    <title>
                      {`${v.name}: ${v.effective_from} to ${v.effective_to ?? "current"}${on ? " — in force on this date" : ""}`}
                    </title>
                    <rect
                      x={x1} y={mid - 6} width={Math.max(4, x2 - x1)} height={12} rx={3}
                      fill={on ? "var(--accent)" : "var(--bg-soft)"}
                      stroke={on ? "var(--accent)" : "var(--border-strong)"}
                      strokeWidth={1}
                    />
                    <text
                      x={x1 + 4} y={mid + 3.5} fontSize={9.5}
                      fill={on ? "white" : "var(--text-3)"}
                    >
                      {v.token ?? v.name}
                    </text>
                    <text x={x1} y={mid + 18} fontSize={9} fill="var(--text-mute)">
                      {short(v.effective_from)}
                    </text>
                  </g>
                );
              })}
              {/* The rules that cite the lit version, hung off its right end. */}
              {lit && lit.rules.length > 0 ? (
                <g>
                  <line
                    x1={xEnd(lit.effective_to)} x2={LABEL_W + AXIS_W + 16}
                    y1={mid} y2={mid} stroke="var(--accent)" strokeWidth={1}
                  />
                  {lit.rules.map((r, i) => {
                    const ry = top + 8 + i * RULE_H;
                    return (
                      <g key={r.rule_id} role="listitem">
                        <title>{`${r.rule_id} — ${r.summary}${r.origin_ref ? ` (${r.origin_ref})` : ""}${r.can_block ? " · may block" : " · may only warn"}`}</title>
                        <line
                          x1={LABEL_W + AXIS_W + 16} x2={LABEL_W + AXIS_W + 28}
                          y1={mid} y2={ry + 6} stroke="var(--border-strong)" strokeWidth={1}
                        />
                        <circle
                          cx={LABEL_W + AXIS_W + 32} cy={ry + 6} r={3}
                          fill={r.can_block ? "var(--danger)" : "var(--warn)"}
                        />
                        <text x={LABEL_W + AXIS_W + 40} y={ry + 9.5} fontSize={10.5} fill="var(--text)">
                          {r.rule_id}
                          <tspan fill="var(--text-3)"> {clip(r.origin_ref?.replace(/^[^ ]+ \d+\/\d{4} ?/, "") ?? "")}</tspan>
                        </text>
                      </g>
                    );
                  })}
                </g>
              ) : lit ? (
                <text x={LABEL_W + AXIS_W + 24} y={mid + 4} fontSize={10} fill="var(--text-mute)">
                  no rules cite it
                </text>
              ) : (
                <text x={LABEL_W + AXIS_W + 24} y={mid + 4} fontSize={10} fill="var(--text-mute)">
                  nothing in force on this date
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div className="play-meta" style={{ marginTop: 8 }}>
        <span>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: "var(--danger)", marginRight: 4 }} />
          may block
        </span>
        <span>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: "var(--warn)", marginRight: 4 }} />
          may only warn
        </span>
        <span>{q.data.pack_version ? `pack ${q.data.pack_version}` : ""}</span>
      </div>

      {unattributed.length > 0 ? (
        <div style={{ marginTop: 10 }} data-testid="lineage-unattributed">
          <div className="section-eyebrow">CITE NO INSTRUMENT YET</div>
          <div className="play-meta">
            <span>
              {unattributed.length} {unattributed.length === 1 ? "rule is" : "rules are"} Xamun
              placeholders awaiting curation. They may warn and may never block, and
              they are here so the map does not look more finished than the rulebook is:{" "}
              {unattributed.map((r) => r.rule_id).join(", ")}.
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
